import React, { useEffect, useState } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import '../styles/DdInstallmentReport.css'

const DdInstallmentReport = () => {
  const superAdminId = Number(localStorage.getItem("adminuserId")) || 14;

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filter, setFilter] = useState("today");   
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Correct Backend Path
  const BASE_URL = "https://narrowitsolution.in/api/installments";

  const API = {
    today: `${BASE_URL}/today?superAdminId=${superAdminId}`,
    tomorrow: `${BASE_URL}/tomorrow?superAdminId=${superAdminId}`,
    upcoming: `${BASE_URL}/upcoming?superAdminId=${superAdminId}`,
    pending: `${BASE_URL}/pending?superAdminId=${superAdminId}`,   
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(API[filter]);
        setRecords(res.data.data || []);
        setError(null);
      } catch (err) {
        setError("Failed to load deposit installments");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filter]);

  // Pagination
  const totalPages = Math.ceil(records.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = records.slice(startIndex, startIndex + rowsPerPage);

  const handlePrev = () =>
    currentPage > 1 && setCurrentPage(currentPage - 1);

  const handleNext = () =>
    currentPage < totalPages && setCurrentPage(currentPage + 1);

  const totals = currentData.reduce(
    (acc, item) => acc + (item.amount || 0),
    0
  );

  const downloadPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.width;

    doc.text("Deposit Installment Due Report", pageWidth / 2, 14, { align: "center" });

    autoTable(doc, {
      startY: 20,
      head: [["Sr", "Customer", "Account", "Installment No", "Amount", "Due Date", "Status"]],
      body: currentData.map((item, i) => [
        startIndex + i + 1,
        item.account?.customerName,
        item.account?.accountNo,
        item.installmentNo,
        item.amount,
        new Date(item.dueDate).toLocaleDateString("en-IN"),
        item.status,
      ]),
      theme: "grid",
    });

    doc.save("DepositInstallments.pdf");
  };

  return (
    <div className="DdInstallmentReport-wrapper">
      <h1 className="EmiDueReport-title">📋 Deposit Installments Report</h1>

      {/* Filters */}
      <div className="DdInstallmentReport-filters">
        {["today", "tomorrow", "upcoming", "pending"].map((f) => (
          <button
            key={f}
            className={`filter-btn ${filter === f ? "active" : ""}`}
            onClick={() => {
              setFilter(f);
              setCurrentPage(1);
            }}
          >
            {f.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Rows per page */}
      <div className="DdInstallmentReport-controls">
        <label>
          Show{" "}
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>{" "}
          rows
        </label>

        <button className="filter-btn" onClick={downloadPDF}>
          📄 PDF
        </button>
      </div>

      {/* Table */}
      <div className="DdInstallmentReport-table-container">
        <table className="DdInstallmentReport-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Customer</th>
              <th>Account No</th>
              <th>Installment No</th>
              <th>Amount</th>
              <th>Due Date</th>
              <th>Status</th>
            </tr>
          </thead>

          <tbody>
            {currentData.length === 0 ? (
              <tr>
                <td colSpan="7" className="DdInstallmentReport-empty">
                  No records found
                </td>
              </tr>
            ) : (
              currentData.map((item, idx) => (
                <tr key={idx}>
                  <td>{startIndex + idx + 1}</td>
                  <td>{item.account?.customerName}</td>
                  <td>{item.account?.accountNo}</td>
                  <td>{item.installmentNo}</td>
                  <td>₹{item.amount}</td>
                  <td>{new Date(item.dueDate).toLocaleDateString("en-IN")}</td>
                  <td>
                    <span
                      className={`DdInstallmentReport-status ${
                        item.status === "Paid" ? "paid" : "pending"
                      }`}
                    >
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>

          {/* Total Row */}
          <tfoot>
            <tr className="EmiDueReport-total-row">
              <td colSpan="4" style={{ textAlign: "right", fontWeight: "bold" }}>
                Total:
              </td>
              <td style={{ fontWeight: "bold" }}>₹{totals.toLocaleString()}</td>
              <td colSpan="2"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Pagination */}
      <div className="DdInstallmentReport-pagination">
        <button
          className="pagination-btn"
          disabled={currentPage === 1}
          onClick={handlePrev}
        >
          ⬅ Prev
        </button>

        <span className="pagination-info">
          Page {currentPage} / {totalPages || 1}
        </span>

        <button
          className="pagination-btn"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={handleNext}
        >
          Next ➡
        </button>
      </div>
    </div>
  );
};

export default DdInstallmentReport;